/**
 * Nächster offener Klärfall der Ansicht (epic-auftragsampel Story 7.20, EXPERIENCE Flow F1): nach dem Lösen eines
 * Klärfalls springt das Seitenpanel (am Handy die eigene Seite) zum nächsten Klärfall in der Reihenfolge der sichtbaren
 * Liste. Reine Funktionen ohne React, Netz und Uhr; Server-Frage und Prüfung „ist noch Klärfall?“ kommen als Funktionen herein.
 * Reine Oberflächenhilfe: keine Schreibaktion, kein Command.
 */

/**
 * Antwort des Servers auf „nächster Klärfall der Ansicht nach diesem Auftrag?“ (`POST …/next`): `row` = gefunden,
 * `none` = keiner mehr in der Ansicht, `unsure` = nicht vollständig durchsucht (z. B. mehr Gruppen als gezeigt).
 */
export type NextAnswer<T> = { kind: 'row'; key: string; target: T } | { kind: 'none' } | { kind: 'unsure' };

export type CaseJump<T> = { kind: 'next'; key: string; target: T } | { kind: 'none' } | { kind: 'unsure' };

/**
 * So viele Kandidaten werden höchstens frisch geprüft (Server-Fragen höchstens eine mehr, wegen der Bestätigung), wenn der Treffer beim frischen Laden doch kein Klärfall mehr ist (ein
 * zweiter Bearbeiter hat ihn gerade gelöst) oder bei der Bestätigung nicht mehr zur Ansicht gehört; Bedien-Konstante der
 * Oberfläche, keine fachliche Grenze.
 */
export const CASE_JUMP_MAX_ASKS = 3;

/**
 * Nächsten Klärfall bestimmen: Der Server sucht ihn frisch in der aktuellen Listenabfrage (Filter, Suche, Sortierung,
 * Gruppen) – eine Quelle für Tablet und Handy, nie gemerkte Zeilen des Browsers (Zweit-Review #557). Der gefundene Auftrag
 * wird zusätzlich frisch geladen (`check`); ist er inzwischen kein Klärfall mehr, wird neu gefragt. Ist er noch Klärfall,
 * wird die Ansicht danach noch einmal gefragt (Bestätigung, Zweit-Review #557): Nur wenn der Server denselben Auftrag
 * wieder nennt, gehört er noch zur Ansicht und es wird gesprungen – fiel er während des Ladens aus der Ansicht (z. B.
 * Filter passt nicht mehr), gilt die neue Antwort (nächster Kandidat bzw. keiner). Die Filterlogik bleibt beim Server.
 * `unsure`, wenn eine Abfrage scheitert, der Server unsicher ist oder die Grenze erreicht ist – dann weder springen noch
 * schließen.
 */
export async function findNextCase<T>(ask: () => Promise<NextAnswer<T>>, check: (key: string) => Promise<boolean>, attempts = CASE_JUMP_MAX_ASKS): Promise<CaseJump<T>> {
  const askSafe = async (): Promise<NextAnswer<T>> => {
    try {
      return await ask();
    } catch {
      return { kind: 'unsure' };
    }
  };
  if (attempts <= 0) return { kind: 'unsure' };
  let answer = await askSafe();
  for (let i = 0; i < attempts; i++) {
    if (answer.kind !== 'row') return answer;
    let open: boolean;
    try {
      open = await check(answer.key);
    } catch {
      return { kind: 'unsure' };
    }
    // Letzter Kandidat ohne Klärfall: nicht weiter fragen.
    if (!open && i === attempts - 1) break;
    // Kein Klärfall mehr: neu fragen; noch Klärfall: dieselbe Frage als Bestätigung, dass er noch zur Ansicht gehört.
    const again = await askSafe();
    if (open && again.kind === 'row' && again.key === answer.key) return { kind: 'next', key: again.key, target: again.target };
    answer = again;
  }
  return answer.kind === 'none' ? answer : { kind: 'unsure' };
}

/** Stand, unter dem eine Sprung-Suche begann (bzw. der jetzt gilt). */
export interface JumpContext {
  /** Angezeigter Auftrag; `null` = Panel bzw. Seite nicht mehr da. */
  orderId: string | null;
  /** Zähler der Sprung-Suchen; „Rückgängig“ erhöht ihn. */
  generation: number;
  /** Ansicht (Reiter, Filter, Suche, Sortierung, Herkunftsgruppe), in der gesucht wurde. */
  viewKey: string;
}

/**
 * Ergebnis einer Sprung-Suche nur anwenden, wenn sich seit ihrem Beginn nichts geändert hat: derselbe Auftrag ist noch
 * zu sehen, kein „Rückgängig“ dazwischen und dieselbe Ansicht – sonst verwerfen (weder springen noch schließen).
 */
export function jumpStillCurrent(started: JumpContext, now: JumpContext): boolean {
  return now.orderId !== null && now.orderId === started.orderId && now.generation === started.generation && now.viewKey === started.viewKey;
}

// ---------------------------------------------------------------------------
// Schalter „Nach dem Lösen zum nächsten Klärfall“: Bedienvorliebe im Browser je Person (Ticket: Standard ein).

const STORAGE_PREFIX = 'till-erp.klaerfall-springen.';

export function readCaseJump(userId: string): boolean {
  try {
    return window.localStorage.getItem(STORAGE_PREFIX + userId) !== 'off';
  } catch {
    return true;
  }
}

export function writeCaseJump(userId: string, on: boolean): void {
  try {
    if (on) window.localStorage.removeItem(STORAGE_PREFIX + userId);
    else window.localStorage.setItem(STORAGE_PREFIX + userId, 'off');
  } catch {
    // Ohne Speicher gilt die Wahl bis zum Neuladen (Modul-Zustand in `order-flow.tsx`).
  }
}
